import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import rfidService, { Vehicle } from '../../../../services/rfidService';
import toast from '../../../../utils/toast';
import { PAGE_SIZE_OPTIONS } from '@/config/constants';
import { TanStackTableViewWithSchema } from '@/components/common/Table';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';

interface VehicleListProps {
  onVehicleSelect?: (vehicle: Vehicle) => void;
  selectable?: boolean;
}

const VehicleList: React.FC<VehicleListProps> = ({
  onVehicleSelect,
  selectable = false,
}) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  const {
    page,
    rowsPerPage,
    searchTerm,
    sortKey,
    sortDirection,
    filters,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    handleFilterChange,
    toDetail,
    toEdit,
    toCreate,
  } = useCompletePaginationNavigation(navigate, '/rfid/vehicles');

  const queryParams = useMemo(() => {
    const apiParams: Record<string, any> = {
      page: page + 1,
      limit: rowsPerPage,
      cf: true,
    };

    if (searchTerm) apiParams.search = searchTerm;

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') apiParams[key] = value;
    });

    if (sortKey) {
      apiParams.ordering = sortDirection === 'desc' ? `-${sortKey}` : sortKey;
    }

    return apiParams;
  }, [page, rowsPerPage, searchTerm, filters, sortKey, sortDirection]);

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await rfidService.getVehicles(queryParams);
      setVehicles(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const handleDelete = async (id: string | number) => {
    try {
      await rfidService.deleteVehicle(String(id));
      toast.success('Vehicle deleted successfully');
      loadVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Failed to delete vehicle');
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Vehicles
        </Typography>
        {!selectable && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => toCreate()}
            sx={{ mr: 2 }}
          >
            Add Vehicle
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <TanStackTableViewWithSchema
            data={vehicles}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onRowClick={selectable ? onVehicleSelect : undefined}
            handleView={!selectable ? (id) => toDetail(String(id)) : undefined}
            handleEdit={!selectable ? (id) => toEdit(String(id)) : undefined}
            handleDelete={!selectable ? handleDelete : undefined}
            confirmDelete={
              !selectable
                ? {
                    title: 'Delete Vehicle',
                    message:
                      'Are you sure you want to delete this vehicle? This action cannot be undone.',
                  }
                : undefined
            }
            loading={loading}
            entity="vehicle"
            enableAPIConfig={true}
            onFilterChange={handleFilterChange}
            filterValues={filters}
            fallbackHeaders={[]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default VehicleList;
